/**
 * Stage-count cards.
 *
 * ===========================================================================
 * AN EXPLICIT, RECORDED EXTENSION — THERE IS NO SUCH COMPONENT IN FIGMA
 * ===========================================================================
 * The brief names "stage-count cards (linking into filtered inquiry views)" as the
 * chart-free substitute for the Dashboard's KPI row, and the P2-T0 gate established
 * that the design contains nothing of the kind: `01/ Entry/ Dashboard` has five
 * `Widget/KPICard` instances with sparklines and period-over-period deltas, all of
 * which are Phase 5 by the brief's own out-of-scope list (§1.3 E2). The owner
 * ratified shipping the chart-free widgets plus these cards as an authored
 * extension.
 *
 * So the visual language is BORROWED, not invented: the card shell is the widget
 * Card (`bg/surface` + `border/default` + `radius/md`), the stage name is the real
 * `Pill/Stage`, and the number uses the `KPI/28` type style that already exists in
 * the file's type ramp. No new colour, no new radius, no new type size.
 *
 * ===========================================================================
 * THE ARITHMETIC IS SHOWN HONESTLY
 * ===========================================================================
 * `stage-counts` returns all SEVEN enum members plus a total, but only five have a
 * board column (the locked stage map, PHASE2-REPORT §2). If `follow_up` or
 * `won_closed` hold rows, the five cards will NOT sum to the total — and a
 * coordinator noticing that would be right to distrust the screen. Those counts are
 * therefore stated in a footnote that also says why they have no card. Hiding them
 * would make the page quietly wrong; inventing a sixth pill colour for them would
 * break hard rule 4.
 *
 * ===========================================================================
 * PHASE 2.5a · LEVER L3 — THIS CARD IS NOW THE BOARD COLUMN HEADER
 * ===========================================================================
 * The stage-colour spine only works if the same stage reads as the same OBJECT
 * everywhere. The board column header is `dot + label + count`
 * (`InquiryBoard.tsx`), so the dashboard card became `dot + label + count` too —
 * the `StagePill` it used before is gone from here.
 *
 * That is a swap, not an addition, and it is deliberate: the pill ALREADY
 * carried a stage dot inside itself, so "give the card its stage dot" could only
 * mean promoting the dot to the card's own level. Two dots would have been
 * absurd, and a pill-inside-a-card is a component wearing another component's
 * clothes — the card is a KPI tile, and the tinted chip in its corner was the
 * one thing stopping it from reading as the same family as the board column it
 * links to.
 *
 * NO NEW COLOUR VALUE ENTERS. The dot uses the wrapper/`bg-current` trick the
 * board already uses so that `stageToneClasses` stays the ONLY place a stage
 * colour is named — see the comment at the dot itself.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchStageCounts, inquiryKeys } from '../../lib/inquiries-api';
import { BOARD_STAGES, STAGES, stageTabLabel, stageToneClasses } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { SkeletonBlock } from '../../components/ui';
import { Alert } from '../../components/ui';
import { cn } from '../../lib/cn';

/** Stages that are real, reachable, and have no card. */
const UNCOLUMNED: readonly InquiryStage[] = ['follow_up', 'won_closed'];

/**
 * The 8px stage dot — the same mark, from the same tokens, as the board column
 * header's `Dot` (12:4413).
 *
 * `stageToneClasses` returns a tint/foreground PAIR and is the only place stage
 * colours are named (`domain/stages.ts`). So the wrapper takes the pair and the
 * inner circle takes `bg-current` — the foreground half — covering the tint
 * completely. This module therefore names no colour at all, which is the rule
 * the single stage map exists to enforce and the reason "add a dot" needed no
 * new token.
 *
 * MEASURED: the stage foregrounds are mode-invariant in Figma, so on the dark
 * card surface (#1f1f1f) the dots ran 2.31:1 (confirm) to 3.25:1 (quote) — two
 * of the five under the 3:1 non-text bar.
 *
 * ===========================================================================
 * PHASE 2.5a · LEVER 7 — FIXED, WITH THE MECHANISM ALREADY IN THE REPO
 * ===========================================================================
 * The note that stood here said the fix "means dark-mode stage foregrounds — a
 * new colour value, which this pass is explicitly not allowed to invent". That
 * conclusion was wrong, and the counter-example was two files away:
 * `InquiryBoard`'s `StageAccent` solves the identical problem with
 * `dark:opacity-0` on the `bg-current` fill. Hiding the foreground in dark mode
 * UNCOVERS the tint that the wrapper was already painting underneath it, so the
 * mark ends up over 3:1 in both themes and NO value is invented — both halves
 * are the stage pair this component was already handed.
 *
 * The dot now does the same thing, so every stage mark in the app — the board's
 * column rule, the board's header dot, this card's dot — swaps halves by one
 * mechanism instead of three:
 *
 *   dot vs the card    LIGHT (stage/…/text)      DARK (stage/…/bg)
 *   ────────────────────────────────────────────────────────────────
 *   new                5.38                      13.89
 *   quote              5.07                      14.70
 *   confirm            7.10                      13.88   (was 2.31)
 *   advance            5.17                      14.38
 *   lost               6.47                      13.49   (was 2.54)
 *
 * The redundancy argument in the previous note still holds and is still why this
 * was never an SC 1.4.1 violation — the stage NAME sits beside the dot and the
 * link's `aria-label` names it. Redundant is not the same as illegible, and a
 * 2.31:1 dot was illegible.
 */
function StageDot({ stage }: { stage: InquiryStage }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-2 shrink-0 rounded-full',
        stageToneClasses(STAGES[stage].tone),
      )}
    >
      {/* `dark:opacity-0` is the whole dark-mode fix. See above, and `StageAccent`
       * in `InquiryBoard.tsx`, which is the same three characters for the same
       * reason. Deleting it does not break a layout; it breaks a contrast ratio. */}
      <span className="size-2 rounded-full bg-current dark:opacity-0" />
    </span>
  );
}

export function StageCountCards({
  countryCode,
  canView,
}: {
  countryCode: string;
  canView: boolean;
}): React.JSX.Element | null {
  const query = useQuery({
    queryKey: inquiryKeys.stageCounts(countryCode),
    queryFn: ({ signal }) => fetchStageCounts(countryCode, signal),
    enabled: canView,
  });

  if (!canView) return null;

  if (query.isPending) {
    return (
      <div
        aria-busy="true"
        aria-label="Loading stage counts"
        className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5"
      >
        {BOARD_STAGES.map((stage) => (
          <div
            key={stage}
            className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5"
          >
            {/* Same two blocks as the loaded card: a label line and a number. */}
            <SkeletonBlock className="h-5 w-28" />
            <SkeletonBlock className="h-8 w-14" />
          </div>
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <Alert tone="danger" title="Stage counts could not be loaded">
        The pipeline summary did not answer. The lists below are unaffected.
      </Alert>
    );
  }

  const counts = query.data.counts;
  const hidden = UNCOLUMNED.filter((stage) => (counts[stage] ?? 0) > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        {BOARD_STAGES.map((stage) => (
          /*
           * LEVER 7 — the hover was `transition-shadow hover:shadow-md`, and it
           * failed two ways at once.
           *
           *  · IT DOES NOT EXIST IN DARK MODE. `shadow-md` is a black shadow; on
           *    `bg/page` #161616 it measures 1.16:1. A dark-mode user got no hover
           *    feedback on these cards at all.
           *  · A SHADOW IS NEITHER `transform` NOR `opacity`, so `transition-shadow`
           *    is outside what hard rule 4 permits to animate.
           *
           * It becomes the board card's hover, verbatim — `border/strong` plus the
           * same 2px lift, from the same tokens, wrapped in the same `motion-safe:`.
           * A stage-count card and the board column it links to now answer a pointer
           * identically, which is the point of the spine.
           *
           *   border/strong on the card   1.41 light · 11.63 dark
           *
           * 1.41:1 in light is deliberate and is not a violation: hover feedback has
           * no WCAG contrast bar, the 2px lift is the light-mode cue, and this is the
           * exact pair `InquiryBoard`'s card already uses. The two modes lead with
           * different halves of the same treatment rather than one mode having none.
           */
          /*
           * `group` ON THE LINK, TRANSLATE ON THE CHILD — the board card's pattern
           * (`InquiryBoard.tsx`), and not decoration. The first draft put `hover:` on
           * the element that translates, and an adversarial sweep along the card's
           * bottom edge measured a 51-cycle enter/leave oscillation: the card slides
           * 2px out from under the pointer, un-hovers, drops back, re-hovers. With
           * the hover test anchored to the static Link, the same sweep measures one
           * enter and zero spurious leaves. The Link stays put; only the visual body
           * moves, so the hit target never escapes the cursor.
           */
          <Link
            key={stage}
            to={`/countries/${countryCode}/inquiries?stage=${stage}`}
            className="group flex rounded-md"
            aria-label={`${stageTabLabel(stage)}: ${String(counts[stage] ?? 0)} inquiries`}
          >
            <span className="flex w-full flex-col gap-3 rounded-md border border-line bg-surface p-5 group-hover:border-line-strong motion-safe:transition-transform motion-safe:group-hover:-translate-y-0.5">
            <span className="flex min-w-0 items-center gap-2">
              <StageDot stage={stage} />
              <span className="truncate text-body font-medium text-fg">
                {stageTabLabel(stage)}
              </span>
            </span>
            {/*
             * L6 · LEVER 7 — the number re-enters when it changes. `key` is the
             * mechanism: text content is not an animatable property, so a
             * transition cannot reach it; remounting on a changed value restarts
             * the keyframe. Same utility and same tokens as the board's column
             * count pill, so one stage's number behaves the same on both screens.
             */}
            <span
              key={counts[stage] ?? 0}
              className="tnum text-kpi text-fg motion-safe:animate-count-in"
            >
              {counts[stage] ?? 0}
            </span>
            </span>
          </Link>
        ))}
      </div>

      <p className="text-label text-fg-muted">
        {query.data.total} inquiries in total.
        {hidden.length === 0
          ? null
          : ` ${hidden
              .map((stage) => `${String(counts[stage] ?? 0)} in ${STAGES[stage].label}`)
              .join(' and ')} — reachable from a record and from the API, but with no board column this phase.`}
      </p>
    </div>
  );
}
